import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { ShieldCheck, KeyRound, AlertTriangle, CheckCircle2, Building2, UserCog } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Input, Label, FieldError, FieldHint } from '@/components/ui/Input'
import { CenterSpinner } from '@/components/ui/Spinner'
import { useToast } from '@/components/ui/ToastProvider'
import { useAuth } from '@/lib/auth/AuthProvider'
import { fetchApi, ApiError, isRealBackend } from '@/lib/api/fetchClient'

/**
 * /set-password?token=...
 *
 * Az invitációs emailben küldött magic link landing oldala.
 * - token validáció (404 ha invalid, 409 ha lejárt/felhasznált)
 * - jelszó kétszeri beírása
 * - sikeres jelszó beállítás → auto-login → /invoices
 */
export function SetPasswordPage() {
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token') ?? ''
  const nav = useNavigate()
  const auth = useAuth()
  const toast = useToast()

  const [loading, setLoading] = useState(true)
  const [invite, setInvite] = useState<{ email: string; fullName: string } | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    let active = true
    if (!token) {
      setError('Hiányzó vagy érvénytelen meghívó link.')
      setLoading(false)
      return () => {
        active = false
      }
    }
    if (!isRealBackend) {
      setError(
        'A meghívás-elfogadás csak valódi backenddel érhető el. (Demo/mock módban nincs invite flow.)',
      )
      setLoading(false)
      return () => {
        active = false
      }
    }
    fetchApi
      .validateInviteToken(token)
      .then((data) => {
        if (!active) return
        setInvite(data)
        setLoading(false)
      })
      .catch((err: unknown) => {
        if (!active) return
        const msg =
          err instanceof ApiError
            ? err.status === 404
              ? 'A meghívó link érvénytelen vagy nem található.'
              : err.status === 409
                ? err.code === 'invite.expired'
                  ? 'A meghívó link már lejárt — kérj újat az admin-tól.'
                  : 'Ezt a meghívót már felhasználták.'
                : err.message
            : err instanceof Error
              ? err.message
              : 'Ismeretlen hiba'
        setError(msg)
        setLoading(false)
      })
    return () => {
      active = false
    }
  }, [token])

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    if (password.length < 8) {
      setError('A jelszó legalább 8 karakter hosszú legyen.')
      return
    }
    if (password !== confirmPassword) {
      setError('A két jelszó nem egyezik.')
      return
    }
    setSubmitting(true)
    try {
      const session = await fetchApi.acceptInvite(token, password)
      auth.login(session)
      toast.show({
        tone: 'success',
        title: 'Sikeres jelszó beállítás',
        description: `Üdv, ${session.user.fullName}!`,
      })
      nav('/invoices', { replace: true })
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Ismeretlen hiba'
      setError(msg)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-slate-50 dark:bg-slate-950">
      <div className="hidden lg:flex flex-col justify-between text-white p-10 relative overflow-hidden cosmic-bg">
        <div className="absolute inset-0 cosmic-grid opacity-40" aria-hidden />
        <div
          className="absolute inset-0 pointer-events-none"
          aria-hidden
          style={{
            background:
              'radial-gradient(ellipse 60% 50% at 50% 50%, rgba(10, 12, 20, 0.7) 0%, transparent 65%)',
          }}
        />
        <div className="relative z-10 flex items-center gap-3">
          <img
            src="/adaptivo-mark.svg"
            alt=""
            width={56}
            height={56}
            className="h-14 w-14 drop-shadow-[0_0_24px_rgba(34,211,238,0.5)]"
          />
          <div>
            <div className="text-lg font-semibold tracking-tightish">Adaptivo Számlakezelő</div>
            <div className="text-[11px] uppercase tracking-[0.18em] text-cyan-300/80">
              Meghívó elfogadása
            </div>
          </div>
        </div>
        <div className="relative z-10 max-w-lg space-y-4">
          <h2 className="text-4xl font-semibold leading-[1.1] tracking-tightish">
            Egyetlen lépés a{' '}
            <span className="bg-gradient-to-r from-cyan-300 via-emerald-300 to-amber-300 bg-clip-text text-transparent">
              fiókod aktiválásához
            </span>
            .
          </h2>
          <p className="text-slate-300 text-[15px] leading-relaxed">
            Állíts be egy erős jelszót — ezzel fogsz bejelentkezni ezután. A jelszót csak Te ismered,
            csak hash-elt formában tároljuk.
          </p>
          <ul className="space-y-2 text-sm text-slate-300">
            <li className="flex items-center gap-2">
              <UserCog className="h-4 w-4 text-cyan-400" /> Legalább 8 karakter
            </li>
            <li className="flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-cyan-400" /> Bcrypt hash, soha plaintext-en nem tároljuk
            </li>
            <li className="flex items-center gap-2">
              <Building2 className="h-4 w-4 text-cyan-400" /> Production-ben SSO + MFA jön
            </li>
          </ul>
        </div>
        <div className="relative z-10 flex items-center justify-end text-xs">
          <span className="text-cyan-400/70 tracking-[0.18em] uppercase text-[10px]">
            Software solutions
          </span>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-10">
        {loading ? (
          <CenterSpinner />
        ) : error && !invite ? (
          <div className="w-full max-w-sm rounded-md border border-amber-200 bg-amber-50 p-6 text-sm text-amber-800">
            <div className="flex items-start gap-2">
              <AlertTriangle className="h-5 w-5 mt-0.5 flex-shrink-0" />
              <div>
                <h2 className="font-semibold mb-1">Nem tudjuk feldolgozni a meghívót</h2>
                <p>{error}</p>
                <Button
                  variant="secondary"
                  size="sm"
                  className="mt-4"
                  onClick={() => nav('/login', { replace: true })}
                >
                  Vissza a bejelentkezéshez
                </Button>
              </div>
            </div>
          </div>
        ) : invite ? (
          <form onSubmit={submit} className="w-full max-w-sm space-y-5">
            <div>
              <h1 className="text-2xl font-semibold text-slate-900 dark:text-slate-100">Jelszó beállítása</h1>
              <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                Üdv, <strong>{invite.fullName}</strong>! Állítsd be a jelszót a bejelentkezéshez.
              </p>
            </div>

            <div className="rounded-md bg-emerald-50 border border-emerald-200 px-3 py-2 text-xs text-emerald-800 flex items-start gap-2">
              <CheckCircle2 className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <div>
                <div className="font-medium">Meghívó érvényes</div>
                <div>Email: {invite.email}</div>
              </div>
            </div>

            <div>
              <Label htmlFor="sp-pass">Új jelszó</Label>
              <Input
                id="sp-pass"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoFocus
                minLength={8}
                required
                autoComplete="new-password"
              />
              <FieldHint>Legalább 8 karakter.</FieldHint>
            </div>
            <div>
              <Label htmlFor="sp-pass2">Jelszó megerősítése</Label>
              <Input
                id="sp-pass2"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                autoComplete="new-password"
              />
            </div>

            {error && <FieldError>{error}</FieldError>}

            <Button
              type="submit"
              fullWidth
              loading={submitting}
              disabled={password.length < 8 || password !== confirmPassword}
              leftIcon={<KeyRound className="h-4 w-4" />}
            >
              Jelszó beállítása és belépés
            </Button>
          </form>
        ) : null}
      </div>
    </div>
  )
}
